export const RATING_EVIDENCE_VERSION = 'rating-evidence-v1';

const RELATIONSHIPS = new Set(['borrower', 'verified_related_entity']);
const STATUSES = new Set(['accepted', 'excluded', 'not_found', 'unavailable', 'pending']);
const text = (value) => typeof value === 'string' ? value.trim() : '';

export const ratingRelationshipLabel = (relationship) => relationship === 'borrower'
  ? 'Borrower'
  : 'Verified related entity';

const sourceUrl = (value) => {
  try {
    const url = new URL(text(value));
    return ['https:', 'http:'].includes(url.protocol) ? url.href : '';
  } catch (_) {
    return '';
  }
};

// Only the backend can establish issuer eligibility. Legacy arrays and model-supplied
// entity_match labels are not substitutes for the versioned acceptance decision.
export function selectRatingEvidence(company = {}) {
  const validation = company?.ratingValidation;
  const supported = validation?.version === RATING_EVIDENCE_VERSION;
  const reportedStatus = supported && STATUSES.has(validation?.status) ? validation.status : 'pending';
  const ratings = reportedStatus === 'accepted' && Array.isArray(company?.externalRatings)
    ? company.externalRatings.filter((entry) => (
      entry?.validation?.version === RATING_EVIDENCE_VERSION &&
      entry.validation.status === 'accepted' &&
      RELATIONSHIPS.has(entry.validation.relationship)
    )).map((entry) => ({
      Agency: text(entry.Agency || entry.agency),
      Long_Term_Rating: text(entry.Long_Term_Rating || entry.longTermRating || entry.rating),
      Outlook: text(entry.Outlook || entry.outlook),
      Last_Update: text(entry.Last_Update || entry.lastUpdate || entry.Date || entry.date),
      rated_entity_name: text(entry.rated_entity_name),
      source_url: sourceUrl(entry.source_url || entry.sourceUrl),
      validation: {
        version: RATING_EVIDENCE_VERSION,
        status: 'accepted',
        relationship: entry.validation.relationship,
      },
    })).filter((entry) => entry.Agency && entry.Long_Term_Rating && entry.rated_entity_name && entry.source_url)
    : [];
  return {
    ratings,
    validation: {
      version: RATING_EVIDENCE_VERSION,
      status: reportedStatus === 'accepted' && !ratings.length ? 'pending' : reportedStatus,
      acceptedCount: ratings.length,
      rejectedCount: supported && Number.isInteger(validation?.rejectedCount) && validation.rejectedCount >= 0
        ? validation.rejectedCount : 0,
    },
  };
}

const normalizedKey = (key) => key.replace(/[^a-z0-9]/gi, '').toLowerCase();

export function withValidatedCreditRating(profile, company) {
  const clean = Object.fromEntries(Object.entries(profile || {}).filter(([key]) => normalizedKey(key) !== 'creditrating'));
  const { ratings } = selectRatingEvidence(company);
  if (ratings.length) {
    clean.creditRating = {
      value: ratings.map((rating) => `${rating.Agency} ${rating.Long_Term_Rating} — ${rating.rated_entity_name} (${ratingRelationshipLabel(rating.validation.relationship)})`).join('; '),
      source: 'Validated external ratings',
    };
  }
  return clean;
}

// These structured containers can carry discovery output, including rejected issuers.
// Do not rewrite independently generated prose or numeric underwriting scores.
const RATING_CONTAINERS = new Set([
  'ratingcandidates', 'rejectedratingcandidates', 'externalratings', 'creditratings',
  'scoringcreditratings', 'geminicreditratings', 'creditrating', 'ratingvalidation',
  'rejectedratings', 'discoveredratings', 'ratingaudit', 'externalratingsaudit',
  'creditratingcandidates',
]);

function removeRawRatingContainers(value) {
  if (Array.isArray(value)) return value.map(removeRawRatingContainers);
  if (!value || typeof value !== 'object' || Object.getPrototypeOf(value) !== Object.prototype) return value;
  return Object.fromEntries(Object.entries(value)
    .filter(([key]) => !RATING_CONTAINERS.has(normalizedKey(key)))
    .map(([key, nested]) => [key, removeRawRatingContainers(nested)]));
}

export function buildRatingSafeAssessmentEntity(entity, company) {
  const clean = removeRawRatingContainers(entity);
  const { ratings, validation } = selectRatingEvidence(company);
  return {
    ...clean,
    businessProfile: withValidatedCreditRating(clean?.businessProfile, company),
    externalRatings: ratings,
    ratingValidation: validation,
  };
}
